import { baseResponseSchema } from 'services/api/api.types';
import { z } from 'zod/v4';

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

const selfExclusionResponseObjectSchema = z.object({
  accountCode: z.string(),
  code: z.string(),
  period: z.number(),
  startTimestamp: z.number(),
  endTimestamp: z.number().optional(),
  type: z.string(),
});

const selfExclusionResponseSchema = baseResponseSchema.extend({
  responseObject: z.union([selfExclusionResponseObjectSchema, z.object({})]),
});

type SelfExclusionIResponse = z.infer<typeof selfExclusionResponseSchema>;

export type SelfExclusionResponseObject = z.infer<
  typeof selfExclusionResponseObjectSchema
>;

type TransformedSelfExclusionResponse = ReturnType<
  typeof transformSelfExclusionResponse
>;

/* -------------------------------------------------------------------------------------------------
 * transformer helper
 * -----------------------------------------------------------------------------------------------*/

const transformSelfExclusionResponse = (response: SelfExclusionIResponse) => {
  const selfExclusion = response.responseObject;
  const now = Date.now();

  const isSelfExclusionActive =
    (selfExclusion?.startTimestamp <= now &&
      selfExclusion?.endTimestamp &&
      selfExclusion?.endTimestamp >= now) ||
    (selfExclusion?.code === 'LOCAL-PERM' && selfExclusion?.period === 0) ||
    selfExclusion.type === 'PERMANENT';

  const isPermanentExclusion = selfExclusion.type === 'PERMANENT';

  const exclusionPeriodInDays = selfExclusion?.period ?? 0;
  const exclusionPeriodInMonths =
    exclusionPeriodInDays >= 30 ? Math.round(exclusionPeriodInDays / 30) : 0;
  const exclusionPeriodInYears =
    exclusionPeriodInMonths >= 12
      ? Math.round(exclusionPeriodInMonths / 12)
      : 0;

  const isRevokeSelfExclusion =
    isSelfExclusionActive &&
    selfExclusion?.code === 'LOCAL-TEMP' &&
    selfExclusion?.period === 30;

  const startTimestamp = selfExclusion?.startTimestamp;
  const hasSixMonthsPassed = startTimestamp
    ? now >= startTimestamp + 6 * 30 * 24 * 60 * 60 * 1000
    : false;
  const has24hSinceStart = startTimestamp
    ? now >= startTimestamp + 24 * 60 * 60 * 1000
    : false;

  return {
    isSelfExclusionActive,
    selfExclusion,
    isPermanentExclusion,
    exclusionPeriodInDays,
    exclusionPeriodInMonths,
    exclusionPeriodInYears,
    isRevokeSelfExclusion,
    hasSixMonthsPassed,
    has24hSinceStart,
  };
};

export { selfExclusionResponseSchema, transformSelfExclusionResponse };
export type { SelfExclusionIResponse, TransformedSelfExclusionResponse };
